import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  HiOutlineClock,
  HiOutlinePlus,
  HiOutlineShare,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineShoppingBag,
  HiOutlineArchiveBoxArrowDown,
} from "react-icons/hi2";
import { GiCarrot, GiMeat, GiCheeseWedge } from "react-icons/gi";
import { useLanguage } from "../../contexts/LanguageContext";
import shoppingListService from "../../services/shoppingListService";

const SECTION_OPTIONS = [
  "Fruits & Légumes",
  "Boucherie",
  "Crémerie",
  "Épicerie",
  "Boulangerie",
  "Boissons",
  "Autre",
];

const SECTION_ICONS = {
  "Fruits & Légumes": GiCarrot,
  Boucherie: GiMeat,
  Crémerie: GiCheeseWedge,
};

const getSectionIcon = (section) => SECTION_ICONS[section] || HiOutlineShoppingBag;

const cardClass =
  "bg-white dark:bg-ink-800 rounded-2xl border border-ink-900/10 dark:border-ink-700 shadow-sm";

const ShoppingListPage = () => {
  const { t } = useLanguage();
  const tt = (key, fallback) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };

  const [list, setList] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAddForm, setShowAddForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    quantity: "",
    section: SECTION_OPTIONS[0],
    calories: "",
  });
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await shoppingListService.getActiveList();
      setList(res.data.list);
      setItems(res.data.items || []);
    } catch (err) {
      console.error("Erreur lors du chargement de la liste de courses:", err);
      setError(
        tt("errorLoadingShoppingList", "Impossible de charger la liste de courses."),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const groupedItems = useMemo(() => {
    const groups = new Map();
    items.forEach((item) => {
      if (!groups.has(item.section)) groups.set(item.section, []);
      groups.get(item.section).push(item);
    });
    return Array.from(groups.entries());
  }, [items]);

  const totalItems = items.length;
  const checkedItems = items.filter((i) => i.is_checked).length;
  const totalCalories = items.reduce(
    (sum, i) => sum + (Number(i.calories) || 0),
    0,
  );
  const hasCalories = items.some(
    (i) => i.calories !== null && i.calories !== undefined,
  );

  const handleToggleItem = async (item) => {
    const nextChecked = !item.is_checked;
    setItems((prev) =>
      prev.map((i) => (i.id === item.id ? { ...i, is_checked: nextChecked } : i)),
    );
    try {
      await shoppingListService.updateItem(item.id, { is_checked: nextChecked });
    } catch (err) {
      console.error("Erreur lors de la mise à jour de l'article:", err);
      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id ? { ...i, is_checked: item.is_checked } : i,
        ),
      );
    }
  };

  const handleDeleteItem = async (itemId) => {
    const previous = items;
    setItems((prev) => prev.filter((i) => i.id !== itemId));
    try {
      await shoppingListService.deleteItem(itemId);
    } catch (err) {
      console.error("Erreur lors de la suppression de l'article:", err);
      setItems(previous);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setSaving(true);
    try {
      await shoppingListService.addItem({
        name: form.name.trim(),
        quantity: form.quantity.trim() || undefined,
        section: form.section,
        calories: form.calories === "" ? undefined : Number(form.calories),
      });
      setForm({ name: "", quantity: "", section: SECTION_OPTIONS[0], calories: "" });
      setShowAddForm(false);
      await loadList();
    } catch (err) {
      console.error("Erreur lors de l'ajout de l'article:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    const lines = groupedItems.map(([section, sectionItems]) => {
      const rows = sectionItems
        .map((i) => `- ${i.name}${i.quantity ? ` (${i.quantity})` : ""}`)
        .join("\n");
      return `${section}\n${rows}`;
    });
    const text = `${tt("myShoppingList", "Ma liste de courses")}\n\n${lines.join("\n\n")}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: tt("myShoppingList", "Ma liste de courses"), text });
        return;
      } catch {
        // annulé par l'utilisateur, on retente le presse-papiers ci-dessous
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      alert(tt("shoppingListCopied", "Liste copiée dans le presse-papiers."));
    } catch (err) {
      console.error("Erreur lors du partage de la liste:", err);
    }
  };

  const handleArchive = async () => {
    if (totalItems === 0) return;
    if (!window.confirm(tt("confirmArchiveList", "Terminer cette liste et en démarrer une nouvelle ?")))
      return;
    try {
      await shoppingListService.archiveActiveList();
      await loadList();
    } catch (err) {
      console.error("Erreur lors de l'archivage de la liste:", err);
    }
  };

  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const res = await shoppingListService.getHistory();
      setHistory(res.data || []);
    } catch (err) {
      console.error("Erreur lors du chargement de l'historique:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      <div className={`${cardClass} p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4`}>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-ink-900 dark:text-white">
            {tt("shoppingListTitle", "Ma Liste de Courses")}
          </h1>
          <p className="text-ink-600 dark:text-ink-300 mt-1 text-sm sm:text-base">
            {tt(
              "shoppingListSubtitle",
              "Organisez vos achats par rayons et ne rien oublier.",
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={openHistory}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-ink-100 hover:bg-ink-200 text-ink-700 dark:bg-ink-700 dark:hover:bg-ink-600 dark:text-white text-sm font-medium transition-colors"
          >
            <HiOutlineClock className="w-4 h-4" />
            {tt("history", "Historique")}
          </button>
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-ember-600 hover:bg-ember-500 text-white text-sm font-semibold transition-colors"
          >
            <HiOutlinePlus className="w-4 h-4" />
            {tt("addItem", "Ajouter un article")}
          </button>
        </div>
      </div>

      {!!error && (
        <div className="bg-red-50 dark:bg-red-500/20 border border-red-200 dark:border-red-300/30 rounded-xl p-4 text-red-700 dark:text-red-100">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-4">
          {loading ? (
            <div className={`${cardClass} p-8 text-center text-ink-500 dark:text-ink-400`}>
              {tt("loading", "Chargement...")}
            </div>
          ) : groupedItems.length === 0 ? (
            <div className={`${cardClass} p-10 text-center`}>
              <HiOutlineShoppingBag className="w-12 h-12 mx-auto mb-3 text-ink-300 dark:text-ink-600" />
              <h3 className="text-lg font-semibold text-ink-900 dark:text-white mb-1">
                {tt("emptyShoppingList", "Votre liste est vide")}
              </h3>
              <p className="text-ink-500 dark:text-ink-400 text-sm">
                {tt("emptyShoppingListHint", "Ajoutez votre premier article pour commencer.")}
              </p>
            </div>
          ) : (
            groupedItems.map(([section, sectionItems]) => {
              const SectionIcon = getSectionIcon(section);
              return (
                <div key={section} className={`${cardClass} p-5`}>
                  <div className="flex items-center gap-2 mb-3 text-ember-600 dark:text-ember-400">
                    <SectionIcon className="w-5 h-5 shrink-0" />
                    <h2 className="text-base font-semibold text-ink-900 dark:text-white">
                      {section}
                    </h2>
                  </div>
                  <ul className="space-y-1">
                    {sectionItems.map((item) => (
                      <li
                        key={item.id}
                        className="group flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-ink-50 dark:hover:bg-ink-700/50 transition-colors"
                      >
                        <button
                          type="button"
                          onClick={() => handleToggleItem(item)}
                          aria-label={
                            item.is_checked
                              ? tt("markAsNotBought", "Marquer comme non acheté")
                              : tt("markAsBought", "Marquer comme acheté")
                          }
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                            item.is_checked
                              ? "bg-ember-600 border-ember-600 text-white"
                              : "border-ink-300 dark:border-ink-600"
                          }`}
                        >
                          {item.is_checked && (
                            <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                              <path
                                fillRule="evenodd"
                                d="M16.7 5.3a1 1 0 010 1.4l-7.4 7.4a1 1 0 01-1.4 0L3.3 9.5a1 1 0 111.4-1.4l3.6 3.6 6.7-6.7a1 1 0 011.4 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </button>
                        <span
                          className={`flex-1 min-w-0 truncate text-sm ${
                            item.is_checked
                              ? "line-through text-ink-400 dark:text-ink-500"
                              : "text-ink-800 dark:text-ink-100"
                          }`}
                        >
                          {item.name}
                        </span>
                        {item.quantity && (
                          <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-ink-100 dark:bg-ink-700 text-ink-600 dark:text-ink-300">
                            {item.quantity}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteItem(item.id)}
                          aria-label={tt("removeItem", "Supprimer l'article")}
                          className="shrink-0 p-1 rounded text-ink-300 hover:text-red-600 dark:text-ink-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <HiOutlineXMark className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })
          )}
        </div>

        <div className={`${cardClass} p-5 space-y-4`}>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-white">
            {tt("shoppingListSummary", "Résumé de la liste")}
          </h2>
          <div className="flex items-center justify-between text-sm">
            <span className="text-ink-500 dark:text-ink-400">
              {tt("totalItems", "Total articles")}
            </span>
            <span className="font-semibold text-ink-900 dark:text-white">
              {totalItems} {tt("items", "items")}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-ink-500 dark:text-ink-400">{tt("checked", "Cochés")}</span>
            <span className="font-semibold text-ink-900 dark:text-white">
              {checkedItems} / {totalItems}
            </span>
          </div>
          {hasCalories && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-ink-500 dark:text-ink-400">
                {tt("totalCalories", "Total Calories")}
              </span>
              <span className="text-2xl font-bold text-ember-600 dark:text-ember-400">
                {totalCalories.toLocaleString("fr-FR")} kcal
              </span>
            </div>
          )}

          <div className="pt-2 space-y-2">
            <button
              type="button"
              onClick={handleShare}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-ink-900/10 dark:border-ink-600 text-ink-700 dark:text-white hover:bg-ink-50 dark:hover:bg-ink-700 text-sm font-medium transition-colors"
            >
              <HiOutlineShare className="w-4 h-4" />
              {tt("shareList", "Partager la liste")}
            </button>
            <button
              type="button"
              onClick={handleArchive}
              disabled={totalItems === 0}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-ink-500 dark:text-ink-400 hover:bg-ink-50 dark:hover:bg-ink-700 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <HiOutlineArchiveBoxArrowDown className="w-4 h-4" />
              {tt("finishList", "Terminer cette liste")}
            </button>
          </div>
        </div>
      </div>

      {showAddForm && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setShowAddForm(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white dark:bg-ink-800 rounded-2xl border border-ink-900/10 dark:border-ink-700 shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-ink-900 dark:text-white">
                {tt("addItem", "Ajouter un article")}
              </h3>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="p-1 rounded text-ink-400 hover:text-ink-700 dark:hover:text-white"
                aria-label={tt("close", "Fermer")}
              >
                <HiOutlineXMark className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddItem} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-1">
                  {tt("itemName", "Nom de l'article")} *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg border border-ink-900/15 dark:border-ink-600 bg-white dark:bg-ink-900 text-ink-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-ember-500/50"
                  placeholder={tt("itemNamePlaceholder", "Ex: Tomates Grappe")}
                  required
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-1">
                    {tt("quantity", "Quantité")}
                  </label>
                  <input
                    type="text"
                    value={form.quantity}
                    onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-ink-900/15 dark:border-ink-600 bg-white dark:bg-ink-900 text-ink-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-ember-500/50"
                    placeholder="500g"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-1">
                    {tt("calories", "Calories")}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.calories}
                    onChange={(e) => setForm((f) => ({ ...f, calories: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-ink-900/15 dark:border-ink-600 bg-white dark:bg-ink-900 text-ink-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-ember-500/50"
                    placeholder={tt("optional", "Optionnel")}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-1">
                  {tt("aisle", "Rayon")}
                </label>
                <select
                  value={form.section}
                  onChange={(e) => setForm((f) => ({ ...f, section: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg border border-ink-900/15 dark:border-ink-600 bg-white dark:bg-ink-900 text-ink-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-ember-500/50"
                >
                  {SECTION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-ink-100 hover:bg-ink-200 text-ink-700 dark:bg-ink-700 dark:hover:bg-ink-600 dark:text-white text-sm font-medium transition-colors"
                >
                  {tt("cancel", "Annuler")}
                </button>
                <button
                  type="submit"
                  disabled={saving || !form.name.trim()}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-ember-600 hover:bg-ember-500 text-white text-sm font-semibold transition-colors disabled:opacity-50"
                >
                  {saving ? tt("saving", "Ajout...") : tt("add", "Ajouter")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showHistory && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setShowHistory(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white dark:bg-ink-800 rounded-2xl border border-ink-900/10 dark:border-ink-700 shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-ink-900 dark:text-white">
                {tt("shoppingListHistory", "Historique des listes")}
              </h3>
              <button
                type="button"
                onClick={() => setShowHistory(false)}
                className="p-1 rounded text-ink-400 hover:text-ink-700 dark:hover:text-white"
                aria-label={tt("close", "Fermer")}
              >
                <HiOutlineXMark className="w-5 h-5" />
              </button>
            </div>
            {historyLoading ? (
              <p className="text-ink-500 dark:text-ink-400 text-sm">
                {tt("loading", "Chargement...")}
              </p>
            ) : history.length === 0 ? (
              <p className="text-ink-500 dark:text-ink-400 text-sm">
                {tt(
                  "noShoppingListHistory",
                  "Aucune liste terminée pour le moment.",
                )}
              </p>
            ) : (
              <ul className="space-y-2">
                {history.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex items-center justify-between rounded-lg bg-ink-50 dark:bg-ink-700/50 px-3 py-2.5 text-sm"
                  >
                    <span className="text-ink-700 dark:text-ink-200">
                      {formatDate(entry.archived_at)}
                    </span>
                    <span className="text-ink-500 dark:text-ink-400">
                      {entry.checked_items} / {entry.total_items} {tt("items", "items")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShoppingListPage;
